import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';

const emptyForm = {
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_number: '',
    notes: ''
};

const Customers = ({ user }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchCustomers();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchCustomers = async () => {
        try {
            const res = await axios.get(`${API}/customers`);
            setCustomers(res.data);
        } catch (error) {
            toast.error('Failed to fetch customers');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setEditingCustomer(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEditDialog = (customer) => {
        setEditingCustomer(customer);
        setForm({
            name: customer.name || '',
            contact_person: customer.contact_person || '',
            phone: customer.phone || '',
            email: customer.email || '',
            address: customer.address || '',
            gst_number: customer.gst_number || '',
            notes: customer.notes || ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            toast.error('Customer name is required');
            return;
        }

        setSaving(true);
        try {
            if (editingCustomer) {
                await axios.put(`${API}/customers/${editingCustomer.id}`, form);
                toast.success('Customer updated');
            } else {
                await axios.post(`${API}/customers`, form);
                toast.success('Customer created');
            }
            setDialogOpen(false);
            fetchCustomers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save customer');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (customer) => {
        if (!window.confirm(`Delete customer "${customer.name}"?`)) return;
        try {
            await axios.delete(`${API}/customers/${customer.id}`);
            toast.success('Customer deleted');
            fetchCustomers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete customer');
        }
    };

    const filteredCustomers = customers.filter(c => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            (c.name || '').toLowerCase().includes(q) ||
            (c.contact_person || '').toLowerCase().includes(q) ||
            (c.phone || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q) ||
            (c.gst_number || '').toLowerCase().includes(q)
        );
    });

    const paginatedCustomers = filteredCustomers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    if (loading) {
        return <div className="p-8">Loading...</div>;
    }

    return (
        <div className="p-8 max-w-7xl mx-auto" data-testid="customers-page">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-stone-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Customers
                    </h1>
                    <p className="text-sm text-stone-500 mt-2 font-medium">
                        Manage your customer directory
                    </p>
                </div>
                <Button
                    onClick={openCreateDialog}
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 transition-all font-medium"
                    data-testid="add-customer-btn"
                >
                    <Plus size={16} className="mr-2" /> Add Customer
                </Button>
            </div>

            <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-stone-100 bg-stone-50/30">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input
                            placeholder="Search by name, contact, phone, email, or GST..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 max-w-sm"
                            data-testid="search-customers"
                        />
                    </div>
                </div>

                {filteredCustomers.length === 0 ? (
                    <div className="text-center py-12 text-[hsl(145,20%,40%)]">
                        <Users size={48} className="mx-auto mb-4 opacity-40" />
                        <p>{searchTerm ? 'No customers match your search' : 'No customers yet. Add your first customer!'}</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Contact Person</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Phone</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">GST Number</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Address</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                    {paginatedCustomers.map((customer) => (
                                        <tr key={customer.id} className="hover:bg-stone-50/50 transition-colors">
                                            <td className="px-4 py-3 text-sm font-medium text-stone-800">{customer.name}</td>
                                            <td className="px-4 py-3 text-sm text-stone-600">{customer.contact_person || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-stone-500 hidden md:table-cell">{customer.phone || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-stone-500 hidden md:table-cell">{customer.email || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-stone-500 hidden lg:table-cell">{customer.gst_number || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-stone-500 hidden lg:table-cell max-w-[200px] truncate">{customer.address || '-'}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openEditDialog(customer)}
                                                        className="h-8 w-8 p-0"
                                                        data-testid={`edit-customer-${customer.id}`}
                                                    >
                                                        <Pencil size={14} />
                                                    </Button>
                                                    {user?.role === 'admin' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(customer)}
                                                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                            data-testid={`delete-customer-${customer.id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-center p-4 border-t border-stone-100 bg-stone-50/30">
                            <Pagination currentPage={currentPage} totalItems={filteredCustomers.length} onPageChange={setCurrentPage} />
                        </div>
                    </>
                )}
            </div>

            {/* Add / Edit Customer Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div>
                            <Label className="mb-1 block">Customer Name *</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Company or individual name"
                                data-testid="customer-name-input"
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="mb-1 block">Contact Person</Label>
                                <Input
                                    value={form.contact_person}
                                    onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                                    placeholder="Point of contact"
                                />
                            </div>
                            <div>
                                <Label className="mb-1 block">Phone</Label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    placeholder="Phone number"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="mb-1 block">Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="Email address"
                                />
                            </div>
                            <div>
                                <Label className="mb-1 block">GST Number</Label>
                                <Input
                                    value={form.gst_number}
                                    onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
                                    placeholder="GST/Tax ID"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="mb-1 block">Address</Label>
                            <Input
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                placeholder="Full address"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block">Notes</Label>
                            <Input
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="Additional notes"
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6 pt-4 border-t border-stone-100">
                            <Button variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 transition-all"
                                data-testid="save-customer-btn"
                            >
                                {saving ? 'Saving...' : (editingCustomer ? 'Update Customer' : 'Create Customer')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Customers;
